// The one way the site asks what is in the catalogue.
//
// Every public gallery used to query Supabase from the browser — the shop grid,
// the collection pages and the related-prints carousel each built their own
// `generation_jobs` query, each repeated the moderation gate, and none of them
// could combine two filters or search for a word. They all go through
// GET /api/prints now. See docs/print-search-api.md in create-prints-node.

function apiBase() {
  return (
    process.env.REACT_APP_CLOUDFLARE_GENERATION_API_URL ||
    process.env.REACT_APP_API_URL ||
    "https://api.createprints.ai/api"
  )
    .replace(/\/api\/?$/, "")
    .replace(/\/+$/, "");
}

/** The attribute filters, in the order the filter sheet shows them. */
export const FACET_NAMES = ["room", "colour", "trait", "impact", "keyword", "palette"];

export const FACET_LABELS = {
  room: "Room",
  colour: "Colour",
  trait: "Mood",
  impact: "Impact",
  keyword: "Subject",
  palette: "Palette",
};

export function buildSearchParams({
  q = "",
  filters = {},
  sort = null,
  limit = 24,
  offset = 0,
  facets = false,
  similarTo = null,
  featured = false,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);

  for (const name of FACET_NAMES) {
    for (const value of filters[name] || []) params.append(name, value);
  }

  if (sort) params.set("sort", sort);
  if (similarTo) params.set("similarTo", similarTo);
  if (featured) params.set("featured", "1");
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (facets) params.set("facets", "1");
  return params;
}

/**
 * Field names the rest of the app already speaks.
 *
 * The API answers in camelCase; the cards, the print page and the structured
 * data were all written against the database's snake_case. Translating in one
 * place is what let the Supabase queries go without touching every consumer.
 */
function toPrint(print) {
  return {
    id: print.id,
    title: print.title,
    description: print.description,
    image_paths: print.imagePaths || [],
    color_category: print.colorCategory,
    recommended_room: print.recommendedRoom || [],
    color_psychology_primary_trait: print.colorPsychologyPrimaryTrait,
    color_psychology_impact_level: print.colorPsychologyImpactLevel,
    keywords: print.keywords || [],
    palette_colors: print.paletteColors || [],
    created_at: print.createdAt,
    // Lets a card reserve its exact space before the image loads.
    width: print.width || null,
    height: print.height || null,
    aspect_ratio: print.aspectRatio || null,
    orientation: print.orientation || null,
  };
}

export async function searchPrints(options = {}, { signal } = {}) {
  const params = buildSearchParams(options);
  const response = await fetch(`${apiBase()}/api/prints?${params.toString()}`, { signal });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data?.error || response.statusText || "Print search failed";
    throw new Error(typeof message === "string" ? message : JSON.stringify(message));
  }

  return {
    prints: (data.prints || []).map(toPrint),
    total: Number(data.total) || 0,
    limit: Number(data.limit) || options.limit || 24,
    offset: Number(data.offset) || 0,
    hasMore: Boolean(data.hasMore),
    facets: data.facets || null,
  };
}

/**
 * Total matches without pulling any prints back.
 *
 * `total` comes with every search, so a pager never needs a second request —
 * this exists only for callers that want the count alone, and asks for the
 * smallest page the API will serve rather than a full one.
 */
export async function countPrints(options = {}, requestOptions = {}) {
  const { total } = await searchPrints({ ...options, limit: 1, offset: 0 }, requestOptions);
  return total;
}
